class GiftSellingOptions extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.customOptionSelects = [];
    this._controllers = [];
    this._onVariantChange = this._onVariantChange.bind(this);
    this._onCustomOptionChange = this._onCustomOptionChange.bind(this);
    this._onFoxThemeReady = this._onFoxThemeReady.bind(this);
  }

  connectedCallback() {
    if (window.FoxTheme && window.FoxTheme.pubsub) {
      this._init();
    } else {
      this._waitForFoxTheme();
    }
  }

  disconnectedCallback() {
    this._teardown();
  }

  _waitForFoxTheme() {
    const checkInterval = setInterval(() => {
      if (window.FoxTheme && window.FoxTheme.pubsub) {
        clearInterval(checkInterval);
        this._init();
      }
    }, 100);

    document.addEventListener('DOMContentLoaded', this._onFoxThemeReady);

    setTimeout(() => {
      clearInterval(checkInterval);
      if (window.FoxTheme && window.FoxTheme.pubsub) {
        this._init();
      } else {
        this._initWithFallback();
      }
    }, 5000);
  }

  _onFoxThemeReady() {
    document.removeEventListener('DOMContentLoaded', this._onFoxThemeReady);
    if (window.FoxTheme && window.FoxTheme.pubsub && !this.initialized) {
      this._init();
    }
  }

  _init() {
    if (this.initialized) return;
    this._teardown();

    this._cacheDOMReferences();
    this.loadVariantsData();

    try {
      this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
        this._onVariantChange
      );

      this.onVariantChangeAfterUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this._onVariantChangeAfterUpdate.bind(this)
      );
    } catch (e) {
      this._initWithFallback();
      return;
    }

    this._listen(this, "change", this._onCustomOptionChange);
    this._setInitialVariant();
    this.initialized = true;
  }

  _initWithFallback() {
    if (this.initialized) return;
    this._teardown();

    this._cacheDOMReferences();
    this.loadVariantsData();

    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      this._listen(variantSelects, 'change', (event) => {
        setTimeout(() => {
          this._onVariantChange({
            data: { event: event, target: event.target, selectedOptionValues: [] }
          });
        }, 50);
      });
    }

    this._listen(this, "change", this._onCustomOptionChange);
    this._setInitialVariant();
    this.initialized = true;
  }

  _cacheDOMReferences() {
    this.customOptionSelects = this.querySelectorAll('[data-custom-option]');
    this.productForm = document.querySelector('form[is="product-form"]');
    this.variantIdInput = this.querySelector('[data-gift-variant-id]');
    this.priceDisplay = this.querySelector('[data-gift-price-display]');

    console.log('Cached DOM references:', {
      customOptionSelects: this.customOptionSelects.length,
      productForm: !!this.productForm,
      variantIdInput: !!this.variantIdInput,
      priceDisplay: !!this.priceDisplay
    });
  }

  loadVariantsData() {
    const variantsScript = this.querySelector('[data-gift-variants]');
    if (variantsScript) {
      try {
        this.variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('Failed to parse variants data:', e);
        this.variants = [];
      }
    }
  }

  _listen(target, type, handler, options) {
    const ctl = new AbortController();
    target.addEventListener(type, handler, { ...(options || {}), signal: ctl.signal });
    this._controllers.push(ctl);
  }

  _teardown() {
    if (!this._controllers) return;

    this._controllers.forEach(ctl => {
      try { ctl.abort(); } catch (_) {}
    });
    this._controllers.length = 0;

    if (this.onVariantChangeUnsubscriber) {
      try { this.onVariantChangeUnsubscriber(); } catch (e) {}
    }
    if (this.onVariantChangeAfterUpdateUnsubscriber) {
      try { this.onVariantChangeAfterUpdateUnsubscriber(); } catch (e) {}
    }
  }

  _onVariantChangeAfterUpdate(message) {
    const { data: { sectionId, html, variant } } = message;

    const productInfo = this.closest("product-info");
    if (productInfo && productInfo.dataset.section !== sectionId) return;

    this._cacheDOMReferences();

    if (html) {
      const newGiftSellingOptions = html.querySelector('gift-selling-options');
      if (newGiftSellingOptions) {
        this.innerHTML = newGiftSellingOptions.innerHTML;
        this._cacheDOMReferences();
        this.loadVariantsData();
      }
    }
  }

  _onVariantChange(message) {
    // For gift products, we handle variant selection through custom options
    // This method is kept for compatibility but main logic is in _onCustomOptionChange
  }

  _onCustomOptionChange(event) {
    console.log('Custom option change event:', event);
    const select = event.target.closest('[data-custom-option]');
    console.log('Found select:', select);

    if (!select || !this.contains(select)) {
      console.log('Select not found or not contained');
      return;
    }

    console.log('Updating selected variant...');
    this.updateSelectedVariant();

    // Update subscription length prices when style or size changes
    if (select.getAttribute('data-custom-option') === 'shirt-style' ||
        select.getAttribute('data-custom-option') === 'shirt-size') {
      this.updateSubscriptionLengthPrices();
    }
  }

  _setInitialVariant() {
    // Set initial variant based on first available combination
    const selectedOptions = this.getSelectedCustomOptions();

    if (selectedOptions.subscriptionLength && selectedOptions.shirtStyle && selectedOptions.shirtSize) {
      this.updateSelectedVariant();
      this.updateSubscriptionLengthPrices();
    } else {
      // Set first radio buttons as checked if none are selected
      const firstLengthRadio = this.querySelector('[data-custom-option="subscription-length"]');
      const firstStyleRadio = this.querySelector('[data-custom-option="shirt-style"]');
      const firstSizeRadio = this.querySelector('[data-custom-option="shirt-size"]');

      if (firstLengthRadio && !firstLengthRadio.checked) {
        firstLengthRadio.checked = true;
      }
      if (firstStyleRadio && !firstStyleRadio.checked) {
        firstStyleRadio.checked = true;
      }
      if (firstSizeRadio && !firstSizeRadio.checked) {
        firstSizeRadio.checked = true;
      }

      this.updateSelectedVariant();
      this.updateSubscriptionLengthPrices();
    }
  }

  updateSelectedVariant() {
    const selectedOptions = this.getSelectedCustomOptions();
    console.log('Selected options:', selectedOptions);

    if (!selectedOptions.subscriptionLength || !selectedOptions.shirtStyle || !selectedOptions.shirtSize) {
      // Not all options selected yet
      console.log('Not all options selected');
      this.updateVariantIdInput('');
      this.updatePriceDisplay(null);
      return;
    }

    // Construct the variant title to match
    const targetTitle = `${selectedOptions.subscriptionLength} - ${selectedOptions.shirtStyle} (${selectedOptions.shirtSize})`;
    console.log('Target title:', targetTitle);
    console.log('Available variants:', this.variants.map(v => v.title));

    // Find matching variant
    const matchedVariant = this.variants.find(variant => variant.title === targetTitle);
    console.log('Matched variant:', matchedVariant);

    if (matchedVariant) {
      // Update the hidden variant ID input
      this.updateVariantIdInput(matchedVariant.id);
      console.log('Updated variant ID to:', matchedVariant.id);

      // Update price display
      this.updatePriceDisplay(matchedVariant);

      // Dispatch custom event for other components
      this.dispatchEvent(new CustomEvent('gift-variant-changed', {
        bubbles: true,
        detail: {
          variant: matchedVariant,
          selectedOptions: selectedOptions
        }
      }));

      // Update main product form
      this._updateProductForm(matchedVariant.id);
    } else {
      console.warn('No matching variant found for:', targetTitle);
      this.updateVariantIdInput('');
      this.updatePriceDisplay(null);
    }
  }

  getSelectedCustomOptions() {
    const subscriptionLengthRadio = this.querySelector('[data-custom-option="subscription-length"]:checked');
    const shirtStyleRadio = this.querySelector('[data-custom-option="shirt-style"]:checked');
    const shirtSizeRadio = this.querySelector('[data-custom-option="shirt-size"]:checked');

    console.log('Found radio buttons:', {
      length: subscriptionLengthRadio,
      style: shirtStyleRadio,
      size: shirtSizeRadio
    });

    const options = {
      subscriptionLength: subscriptionLengthRadio?.value || '',
      shirtStyle: shirtStyleRadio?.value || '',
      shirtSize: shirtSizeRadio?.value || ''
    };

    console.log('Option values:', options);
    return options;
  }

  updateVariantIdInput(variantId) {
    if (this.variantIdInput) {
      this.variantIdInput.value = variantId;
    }
  }

  updatePriceDisplay(variant) {
    if (!this.priceDisplay) return;

    if (variant && variant.price !== undefined) {
      // Format the price using FoxTheme's currency formatting if available
      if (window.FoxTheme && window.FoxTheme.Currency && window.FoxTheme.Currency.formatMoney) {
        const moneyFormat = window.theme?.moneyFormat || (window.FoxTheme.theme && window.FoxTheme.theme.moneyFormat) || '${{amount}}';
        this.priceDisplay.innerHTML = `Price: ${window.FoxTheme.Currency.formatMoney(variant.price, moneyFormat)}`;
      } else {
        // Fallback to basic formatting - prices are typically in cents
        const formattedPrice = (variant.price / 100).toFixed(2);
        this.priceDisplay.innerHTML = `Price: $${formattedPrice}`;
      }
    } else {
      this.priceDisplay.innerHTML = 'Please select all options';
    }
  }

  _updateProductForm(variantId) {
    // Update the buy-buttons variant input (primary target)
    const buyButtonVariantInput = document.querySelector('.product-variant-id');
    if (buyButtonVariantInput) {
      buyButtonVariantInput.value = variantId;
      buyButtonVariantInput.disabled = !variantId;
      console.log('Updated buy-buttons variant input to:', variantId);
    }

    // Update the main product form if it exists
    if (this.productForm) {
      let mainVariantInput = this.productForm.querySelector('input[name="id"]');
      if (!mainVariantInput) {
        mainVariantInput = document.createElement('input');
        mainVariantInput.type = 'hidden';
        mainVariantInput.name = 'id';
        this.productForm.appendChild(mainVariantInput);
      }
      mainVariantInput.value = variantId;
      console.log('Updated main form variant input to:', variantId);
    }

    // Also look for data-main-variant-id-input (for compatibility)
    const mainVariantIdInput = document.querySelector('[data-main-variant-id-input]');
    if (mainVariantIdInput) {
      mainVariantIdInput.value = variantId;
      console.log('Updated data-main-variant-id-input to:', variantId);
    }

    // Update URL params
    const url = new URL(window.location);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, "", url.toString());

    // Dispatch form change event
    if (this.productForm) {
      this.productForm.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateSubscriptionLengthPrices() {
    // Get currently selected style and size
    const selectedOptions = this.getSelectedCustomOptions();

    if (!selectedOptions.shirtStyle || !selectedOptions.shirtSize) {
      console.log('Style or size not selected, cannot update prices');
      return;
    }

    // Find all subscription length price elements
    const priceElements = this.querySelectorAll('[data-price-for-length]');

    priceElements.forEach(priceElement => {
      const length = priceElement.getAttribute('data-price-for-length');

      // Construct variant title for this combination
      const targetTitle = `${length} - ${selectedOptions.shirtStyle} (${selectedOptions.shirtSize})`;

      // Find matching variant
      const matchedVariant = this.variants.find(variant => variant.title === targetTitle);

      if (matchedVariant) {
        // Update price display
        if (window.FoxTheme && window.FoxTheme.Currency && window.FoxTheme.Currency.formatMoney) {
          const moneyFormat = window.theme?.moneyFormat || (window.FoxTheme.theme && window.FoxTheme.theme.moneyFormat) || '${{amount}}';
          priceElement.innerHTML = window.FoxTheme.Currency.formatMoney(matchedVariant.price, moneyFormat);
        } else {
          // Fallback to basic formatting
          const formattedPrice = (matchedVariant.price / 100).toFixed(2);
          priceElement.innerHTML = `$${formattedPrice}`;
        }
      } else {
        priceElement.innerHTML = '—';
      }
    });

    console.log('Updated subscription length prices for:', selectedOptions.shirtStyle, selectedOptions.shirtSize);
  }
}

if (!customElements.get('gift-selling-options')) {
  customElements.define('gift-selling-options', GiftSellingOptions);
}